// Low-Poly-Bausteine aus Grundkörpern. Alle Objekte stehen mit der Unterkante auf y = 0.
import * as THREE from "three"
import { COLORS } from "./palette.js"

const materials = new Map()

export function mat(color, options = {}) {
  const key = `${color}-${JSON.stringify(options)}`
  if (!materials.has(key)) {
    materials.set(key, new THREE.MeshStandardMaterial({ color, flatShading: true, roughness: 0.85, metalness: 0, ...options }))
  }
  return materials.get(key)
}

function mesh(geometry, color, options) {
  const m = new THREE.Mesh(geometry, typeof color === "number" ? mat(color, options) : color)
  m.castShadow = true
  m.receiveShadow = true
  return m
}

export function box(w, h, d, color, options) {
  const m = mesh(new THREE.BoxGeometry(w, h, d), color, options)
  m.position.y = h / 2
  return m
}

export function cylinder(rTop, rBottom, h, color, segments = 10, options) {
  const m = mesh(new THREE.CylinderGeometry(rTop, rBottom, h, segments), color, options)
  m.position.y = h / 2
  return m
}

export function cone(r, h, color, segments = 7) {
  const m = mesh(new THREE.ConeGeometry(r, h, segments), color)
  m.position.y = h / 2
  return m
}

export function at(object, x, y, z, rotationY = 0) {
  object.position.x += x
  object.position.y += y
  object.position.z += z
  object.rotation.y += rotationY
  return object
}

export function group(...children) {
  const g = new THREE.Group()
  children.forEach((child) => g.add(child))
  return g
}

// Dreiecksprisma als Satteldach / Sheddach-Element, Länge entlang z.
export function prism(w, h, d, color, asymmetric = false) {
  const shape = new THREE.Shape()
  shape.moveTo(-w / 2, 0)
  shape.lineTo(w / 2, 0)
  shape.lineTo(asymmetric ? w / 2 : 0, h)
  shape.closePath()
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: d, bevelEnabled: false })
  geometry.translate(0, 0, -d / 2)
  return mesh(geometry, color)
}

export function hall({ w = 8, d = 6, h = 3, wall = COLORS.wall, roof = COLORS.roof, sawtooth = 0 }) {
  const g = group(box(w, h, d, wall))
  if (sawtooth > 0) {
    const tooth = w / sawtooth
    for (let i = 0; i < sawtooth; i++) {
      g.add(at(prism(tooth, 1.1, d, roof, true), -w / 2 + tooth * (i + 0.5), h, 0))
    }
  } else {
    g.add(at(box(w + 0.2, 0.25, d + 0.2, roof), 0, h, 0))
  }
  // Tore und Fensterband
  g.add(at(box(1.4, 1.8, 0.1, COLORS.steelDark), -w / 4, 0, d / 2 + 0.03))
  g.add(at(box(1.4, 1.8, 0.1, COLORS.steelDark), w / 4, 0, d / 2 + 0.03))
  g.add(at(box(w * 0.85, 0.35, 0.08, COLORS.glass), 0, h - 0.8, d / 2 + 0.03))
  g.userData.roof = { w, d, y: h + (sawtooth > 0 ? 0.6 : 0.25) }
  return g
}

export function chimney(h = 6, r = 0.45) {
  const g = group(cylinder(r * 0.8, r, h, COLORS.brick, 8))
  g.add(at(cylinder(r * 0.85, r * 0.85, 0.4, COLORS.white, 8), 0, h * 0.62, 0))
  g.add(at(cylinder(r * 0.82, r * 0.82, 0.4, COLORS.white, 8), 0, h * 0.82, 0))
  g.userData.smokeTop = new THREE.Vector3(0, h + 0.2, 0)
  return g
}

export function silo(h = 5, r = 1, color = COLORS.steel) {
  const g = group(cylinder(r, r, h, color, 10))
  g.add(at(cone(r * 1.02, r * 0.8, color, 10), 0, h, 0))
  return g
}

export function tree(scale = 1, color = COLORS.tree) {
  const g = group(cylinder(0.12, 0.15, 0.6, COLORS.trunk, 5))
  g.add(at(cone(0.75, 1.4, color, 6), 0, 0.45, 0))
  g.add(at(cone(0.55, 1.1, color === COLORS.tree ? COLORS.treeLight : color, 6), 0, 1.15, 0))
  g.scale.setScalar(scale)
  return g
}

export function roundTree(scale = 1) {
  const g = group(cylinder(0.12, 0.15, 0.8, COLORS.trunk, 5))
  const crown = mesh(new THREE.IcosahedronGeometry(0.7, 0), COLORS.treeLight)
  crown.position.y = 1.3
  g.add(crown)
  g.scale.setScalar(scale)
  return g
}

export function wheel(r = 0.22) {
  const m = mesh(new THREE.CylinderGeometry(r, r, 0.18, 8), COLORS.navy)
  m.rotation.x = Math.PI / 2
  m.position.y = r
  return m
}

// Fahrzeuge zeigen in +x-Richtung.
export function truck(color = COLORS.white, cab = COLORS.green) {
  const g = group(at(box(2.4, 1.2, 1, color), -0.3, 0.25, 0), at(box(0.8, 0.95, 0.95, cab), 1.35, 0.25, 0))
  g.add(at(box(0.1, 0.35, 0.8, COLORS.glass), 1.76, 0.75, 0))
  for (const [x, z] of [[-1, 0.5], [-0.2, 0.5], [1.3, 0.5], [-1, -0.5], [-0.2, -0.5], [1.3, -0.5]]) {
    g.add(at(wheel(), x, 0, z))
  }
  return g
}

export function car(color = COLORS.red) {
  const g = group(at(box(1.3, 0.4, 0.7, color), 0, 0.15, 0), at(box(0.7, 0.32, 0.6, color), -0.1, 0.55, 0))
  g.add(at(box(0.72, 0.22, 0.62, COLORS.glass), -0.1, 0.6, 0))
  for (const [x, z] of [[-0.4, 0.33], [0.4, 0.33], [-0.4, -0.33], [0.4, -0.33]]) g.add(at(wheel(0.15), x, 0, z))
  return g
}

export function bike(color = COLORS.lime) {
  const g = group()
  for (const x of [-0.3, 0.3]) {
    const w = mesh(new THREE.TorusGeometry(0.2, 0.04, 4, 10), COLORS.navy)
    w.position.set(x, 0.22, 0)
    g.add(w)
  }
  g.add(at(box(0.6, 0.06, 0.06, color), 0, 0.35, 0))
  g.add(at(box(0.18, 0.35, 0.18, COLORS.navy600), 0, 0.45, 0))
  g.add(at(mesh(new THREE.SphereGeometry(0.11, 6, 5), COLORS.wallWarm), 0, 0.9, 0))
  return g
}

export function house(color = COLORS.wallWarm, roof = COLORS.brick) {
  const g = group(box(1.8, 1.4, 1.6, color))
  g.add(at(prism(2, 0.9, 1.8, roof), 0, 1.4, 0))
  g.children.at(-1).rotation.y = 0
  g.add(at(box(0.35, 0.6, 0.05, COLORS.navy600), 0.3, 0, 0.82))
  g.add(at(box(0.4, 0.35, 0.05, COLORS.glass), -0.4, 0.75, 0.82))
  return g
}

export function windTurbine(h = 9) {
  const g = group(cylinder(0.12, 0.25, h, COLORS.white, 8))
  g.add(at(box(0.9, 0.45, 0.45, COLORS.white), 0.15, h, 0))
  const rotor = new THREE.Group()
  rotor.position.set(-0.35, h + 0.22, 0)
  for (let i = 0; i < 3; i++) {
    const blade = box(0.08, 3.6, 0.32, COLORS.white)
    blade.position.y = 1.8
    const arm = new THREE.Group()
    arm.rotation.x = (i * Math.PI * 2) / 3
    arm.add(blade)
    rotor.add(arm)
  }
  rotor.add(mesh(new THREE.SphereGeometry(0.2, 6, 5), COLORS.green))
  g.add(rotor)
  g.userData.rotor = rotor
  return g
}

export function pylon(h = 6) {
  const g = group(cylinder(0.08, 0.5, h, COLORS.steel, 4))
  g.add(at(box(2.4, 0.12, 0.12, COLORS.steel), 0, h * 0.85, 0))
  g.add(at(box(1.6, 0.12, 0.12, COLORS.steel), 0, h * 0.65, 0))
  g.userData.top = h * 0.85
  return g
}

export function plane(color = COLORS.white) {
  const body = mesh(new THREE.CylinderGeometry(0.35, 0.25, 4, 8), color)
  body.rotation.z = Math.PI / 2
  const nose = mesh(new THREE.ConeGeometry(0.35, 0.7, 8), color)
  nose.rotation.z = -Math.PI / 2
  nose.position.x = 2.35
  const g = group(body, nose)
  g.add(at(box(1, 0.08, 4.6, COLORS.slate), 0.1, -0.05, 0))
  g.add(at(box(0.5, 0.06, 1.6, COLORS.slate), -1.7, 0.05, 0))
  g.add(at(box(0.6, 0.9, 0.08, COLORS.green), -1.75, 0.1, 0))
  return g
}

export function container(color = COLORS.rust) {
  return box(2.4, 1, 1, color)
}

export function crate(color = COLORS.wallWarm) {
  return box(0.8, 0.8, 0.8, color)
}

export function cow() {
  const g = group(at(box(0.9, 0.5, 0.45, COLORS.white), 0, 0.35, 0), at(box(0.3, 0.3, 0.3, COLORS.white), 0.55, 0.6, 0))
  g.add(at(box(0.3, 0.2, 0.47, COLORS.navy), -0.1, 0.55, 0))
  for (const [x, z] of [[-0.3, 0.15], [0.3, 0.15], [-0.3, -0.15], [0.3, -0.15]]) g.add(at(box(0.1, 0.35, 0.1, COLORS.navy600), x, 0, z))
  return g
}

export function towerCrane(h = 9, color = COLORS.orange) {
  const g = group(box(0.4, h, 0.4, color))
  g.add(at(box(7, 0.35, 0.35, color), 2, h, 0))
  g.add(at(box(1, 0.6, 0.6, COLORS.steelDark), -1.2, h - 0.3, 0))
  g.userData.rope = at(box(0.04, 3, 0.04, COLORS.navy), 4.5, h - 3, 0)
  g.userData.load = at(box(0.8, 0.5, 0.8, COLORS.wallWarm), 4.5, h - 3.5, 0)
  g.userData.hook = new THREE.Vector3(4.5, h - 3, 0)
  g.add(g.userData.rope, g.userData.load)
  return g
}

export function excavator() {
  const g = group(at(box(1.6, 0.4, 1.1, COLORS.navy600), 0, 0, 0), at(box(1.2, 0.8, 1, COLORS.orange), -0.1, 0.4, 0))
  const arm = at(box(1.6, 0.2, 0.2, COLORS.orange), 1.1, 1.1, 0)
  arm.rotation.z = -0.5
  g.add(arm)
  const bucket = at(box(0.4, 0.4, 0.5, COLORS.steelDark), 1.75, 0.2, 0)
  g.add(bucket)
  g.userData.arm = arm
  g.userData.bucket = bucket
  return g
}

export function heap(r = 1.4, color = COLORS.gravel) {
  const m = mesh(new THREE.ConeGeometry(r, r * 0.8, 7), color)
  m.position.y = r * 0.4
  return m
}

export function fieldPatch(w, d, color) {
  const m = box(w, 0.08, d, color)
  m.castShadow = false
  return m
}

export function solarPanel() {
  const panel = box(1.1, 0.06, 0.7, COLORS.solar, { metalness: 0.3, roughness: 0.4 })
  panel.rotation.x = -0.35
  return panel
}

export function flag(color = COLORS.green) {
  const g = group(cylinder(0.05, 0.05, 2.2, COLORS.white, 5))
  const cloth = box(0.9, 0.55, 0.04, color)
  cloth.position.set(0.45, 1.85, 0)
  g.add(cloth)
  g.userData.cloth = cloth
  return g
}

export function arrowRing(radius = 2.2, color = COLORS.lime) {
  const g = new THREE.Group()
  for (let i = 0; i < 3; i++) {
    const arc = mesh(new THREE.TorusGeometry(radius, 0.14, 5, 18, Math.PI * 0.5), color, { emissive: color, emissiveIntensity: 0.25 })
    arc.rotation.z = (i * Math.PI * 2) / 3
    const head = mesh(new THREE.ConeGeometry(0.32, 0.6, 5), color, { emissive: color, emissiveIntensity: 0.25 })
    const angle = (i * Math.PI * 2) / 3 + Math.PI * 0.5
    head.position.set(Math.cos(angle) * radius, Math.sin(angle) * radius, 0)
    head.rotation.z = angle
    g.add(arc, head)
  }
  g.rotation.x = -Math.PI / 2
  return g
}

const SKIN = [0xf2c9a0, 0xe0ac7e, 0xc68857, 0x8d5a3b, 0xf5d7bd]

// Kleine Figur, ca. 1,2 Einheiten hoch. helmet: Farbe oder null.
export function person({ shirt = COLORS.green, helmet = null, skin = SKIN[0] } = {}) {
  const g = group(box(0.26, 0.4, 0.16, COLORS.navy600))
  g.add(at(box(0.36, 0.42, 0.22, shirt), 0, 0.4, 0))
  // Arme hängen an einem Schultergelenk, damit sie winken oder sich strecken können
  g.userData.arms = [-0.23, 0.23].map((x) => {
    const shoulder = new THREE.Group()
    shoulder.position.set(x, 0.8, 0)
    const arm = box(0.09, 0.38, 0.1, shirt)
    arm.position.y = -0.19
    shoulder.add(arm)
    g.add(shoulder)
    return shoulder
  })
  const head = mesh(new THREE.IcosahedronGeometry(0.16, 1), skin)
  head.position.y = 1.0
  g.add(head)
  if (helmet !== null) g.add(at(cylinder(0.17, 0.19, 0.1, helmet, 8), 0, 1.08, 0))
  return g
}

export const SKIN_TONES = SKIN
